import { cards } from '../game/cards';
import type { GameAction, GameState, PendingChoice } from '../game/types';
import { actionButtonHTML } from './action-ui';
import { hasFoilFinish } from './foil';
import { icon } from './icons';
import type { CatalogCard, Preferences } from './types';
import './card-inspector.css';
import './mobile-upgrade.css';

export interface InspectionContext { cardId: string; foil: boolean; instanceId?: string }
export interface CardInspectorModel {
  context: InspectionContext;
  card: CatalogCard;
  preferences: Preferences;
  actions: GameAction[];
  phase: GameState['phase'];
  pending?: PendingChoice;
  busy: boolean;
}
interface Point { x: number; y: number }
interface Gesture { zoom: number; pan: Point; center: Point; distance: number }
const escape = (value: string) => value.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

/** Persistent, purely presentational inspection. Callers supply current legal
 * actions; moving, zooming, opening or closing the inspector never submits one. */
export class CardInspector {
  readonly element: HTMLElement;
  private readonly viewport: HTMLElement;
  private readonly imageFrame: HTMLElement;
  private readonly image: HTMLImageElement;
  private readonly copy: HTMLElement;
  private readonly actionHost: HTMLElement;
  private readonly zoomOutput: HTMLOutputElement;
  private readonly resizeObserver: ResizeObserver;
  private model?: CardInspectorModel;
  private identity = '';
  private detailsStamp = '';
  private actionsStamp = '';
  private zoom = 1;
  private pan: Point = { x: 0, y: 0 };
  private fit: Point = { x: 250, y: 343 };
  private ratio = 250 / 343;
  private previewFoil: boolean | null = null;
  private readonly pointers = new Map<number, Point>();
  private readonly touching = new Set<number>();
  private gesture?: Gesture;
  private quickTap?: {id:number;point:Point};
  private buttonPress?: { pointer: number; start: Point; button: HTMLButtonElement; moved: boolean };
  private blockClicksUntil = 0;
  private lastFocus?: HTMLElement;
  private focusFrame = 0;
  private readonly blur = () => this.cancelGesture();

  constructor(private readonly callbacks: { close: () => void; action: (action: GameAction) => void }) {
    this.element = document.createElement('div');
    this.element.className = 'modal-backdrop card-inspector';
    this.element.innerHTML = `<section class="modal inspect-modal" role="dialog" aria-modal="true" aria-label="Card inspection">
      <header class="modal-header"><div class="ci-heading">${icon('cards')}<button type="button" class="ci-details-toggle" data-inspector-control="details" aria-expanded="false">Card details</button></div><button type="button" class="close-modal" data-inspector-control="close" aria-label="Close card inspector">${icon('close')}</button></header>
      <div class="inspect-content"><div class="inspect-stage">
        <div class="ci-viewport" tabindex="0" aria-label="Card image. Pinch or use plus and minus to zoom; drag to pan."><div class="inspect-image"><img draggable="false" alt=""></div></div>
        <div class="ci-zoom-toolbar" role="group" aria-label="Card zoom"><button type="button" data-inspector-control="zoom-out" aria-label="Zoom out">−</button><output class="ci-zoom-value" aria-label="Card zoom level">1×</output><button type="button" data-inspector-control="zoom-in" aria-label="Zoom in">+</button><button type="button" data-inspector-control="fit" class="ci-fit">Fit</button><span class="ci-zoom-hint">Pinch to zoom</span></div>
      </div><div class="inspect-copy" tabindex="0" aria-label="Card details"></div></div>
      <footer class="ci-actions" aria-label="Available card actions"></footer>
    </section>`;
    this.viewport = this.element.querySelector('.ci-viewport')!;
    this.imageFrame = this.element.querySelector('.inspect-image')!;
    this.image = this.imageFrame.querySelector('img')!;
    this.copy = this.element.querySelector('.inspect-copy')!;
    this.actionHost = this.element.querySelector('.ci-actions')!;
    this.zoomOutput = this.element.querySelector('output')!;
    this.element.addEventListener('pointerdown', event => this.trackPointerDown(event), true);
    this.element.addEventListener('pointermove', event => this.trackPointerMove(event), true);
    this.element.addEventListener('pointerup', event => this.trackPointerEnd(event), true);
    this.element.addEventListener('pointercancel', () => this.cancelGesture(), true);
    this.element.addEventListener('click', event => this.handleClick(event), true);
    this.element.addEventListener('keydown', event => this.handleKey(event), true);
    this.element.addEventListener('focusin', event => { if (event.target instanceof HTMLElement) this.lastFocus = event.target; });
    this.viewport.addEventListener('pointerdown', event => this.beginGesture(event));
    this.viewport.addEventListener('pointermove', event => this.moveGesture(event));
    this.viewport.addEventListener('pointerup', event => this.endGesture(event));
    this.viewport.addEventListener('lostpointercapture', event => this.endGesture(event));
    this.viewport.addEventListener('wheel', event => {
      event.preventDefault();
      this.zoomAt(this.zoom * Math.exp(-event.deltaY * .002), this.relative({ x: event.clientX, y: event.clientY }));
    }, { passive: false });
    this.image.addEventListener('load', () => {
      if (this.image.naturalWidth && this.image.naturalHeight) this.ratio = this.image.naturalWidth / this.image.naturalHeight;
      this.measure();
    });
    this.resizeObserver = new ResizeObserver(() => { this.cancelGesture(); this.measure(); });
    this.resizeObserver.observe(this.viewport);
    window.addEventListener('blur', this.blur);
  }

  update(model: CardInspectorModel) {
    const identity = `${model.context.instanceId ?? 'catalog'}:${model.context.cardId}:${model.context.foil}`;
    const changed = identity !== this.identity;
    this.model = model;
    this.element.classList.toggle('ci-reduced-motion', model.preferences.reducedMotion);
    this.element.dataset.cardId = model.context.cardId;
    if (changed) {
      this.identity = identity;this.element.classList.remove('ci-details-open');this.element.querySelector('[data-inspector-control=details]')?.setAttribute('aria-expanded','false');
      this.zoom = 1;
      this.pan = { x: 0, y: 0 };
      this.ratio = 250 / 343;
      this.previewFoil = null;
      this.cancelGesture();
      this.copy.scrollTop = 0;
      this.lastFocus = undefined;
      this.image.src = model.card.image;
      this.image.alt = model.card.name;
    }
    const definition = cards[model.context.cardId];
    const detailStamp = JSON.stringify([model.card, definition?.originalText, definition?.stages, definition?.pur]);
    if (detailStamp !== this.detailsStamp) {
      this.detailsStamp = detailStamp;
      const stages = definition?.stages ?? model.card.powerLevels;
      this.copy.innerHTML = `<div class="eyebrow">SAIYAN SAGA <span>/</span> ${String(model.card.number).padStart(3, '0')}</div>
        <h3 id="card-inspector-title">${escape(model.card.name)}</h3>
        <div class="card-tags"><span class="type-tag">${escape(model.card.type)}</span><span>${escape(model.card.style || 'Freestyle')}</span><span>${escape(({ C: 'Common', U: 'Uncommon', R: 'Rare', UR: 'Ultra rare', P: 'Promo' } as Record<string, string>)[model.card.rarity] ?? model.card.rarity)}</span></div>
        <div class="printed-power"><span class="section-label">PRINTED POWER</span><p>${escape(definition?.originalText || model.card.text || 'Read the preserved original scan for its printed power.')}</p></div>
        ${stages?.length ? `<div class="power-levels"><span>POWER STAGES <b>PUR ${definition?.pur ?? model.card.pur ?? '—'}</b></span><p>${stages.map(value => `<i>${value.toLocaleString()}</i>`).join('')}</p></div>` : ''}
        <button type="button" class="secondary foil-control" id="foil-preview-toggle" data-inspector-control="foil" aria-pressed="false">${icon('spark')}<span class="ci-foil-label">Preview foil finish</span><span class="mini-switch" aria-hidden="true"></span></button>
        <p class="fine-print">Original Score print · 2000</p>`;
      this.element.style.setProperty('--ci-tone', model.card.type.includes('Energy') ? '#eec978' : model.card.type.includes('Physical') ? '#ed887d' : model.card.type === 'Dragon Ball' ? '#e7b469' : '#88d3d4');
    }
    const actionStamp = JSON.stringify([model.actions, model.phase, model.pending]);
    if (actionStamp !== this.actionsStamp) {
      this.actionsStamp = actionStamp;
      this.actionHost.innerHTML = model.actions.length
        ? `<div class="ci-action-list">${model.actions.map((action, index) => actionButtonHTML(action, model, { disabled: model.busy, entering: false, index })).join('')}</div>`
        : '';
    }
    this.actionHost.hidden = !model.actions.length;
    this.actionHost.querySelectorAll<HTMLButtonElement>('[data-action]').forEach(button => {
      button.disabled = model.busy;
      button.type = 'button';
      const action = model.actions.find(value => value.id === button.dataset.action);
      const label = button.querySelector('strong');
      if (action && label) {
        const name = cards[action.cardId ?? '']?.name ?? model.card.name;
        label.textContent = action.kind === 'play-card' ? `${model.phase === 'defend' ? 'Defend with' : 'Play'} ${name}` : action.label;
      }
    });
    this.renderFoil();
    this.measure();
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = requestAnimationFrame(() => {
      if (!this.element.isConnected || this.element.contains(document.activeElement)) return;
      const focus = this.lastFocus?.isConnected ? this.lastFocus : this.element.querySelector<HTMLElement>('[data-inspector-control="close"]');
      focus?.focus({ preventScroll: true });
    });
  }

  debugState() {
    return { cardId: this.model?.context.cardId ?? null, instanceId: this.model?.context.instanceId ?? null,
      foil: this.imageFrame.classList.contains('foil-preview'), zoom: Number(this.zoom.toFixed(3)),
      pan: { x: Number(this.pan.x.toFixed(2)), y: Number(this.pan.y.toFixed(2)) },
      fit: { width: Number(this.fit.x.toFixed(2)), height: Number(this.fit.y.toFixed(2)) },
      pointers: this.pointers.size, actions: this.model?.actions.map(action => action.id) ?? [] };
  }
  destroy() {
    this.cancelGesture();
    cancelAnimationFrame(this.focusFrame);
    this.resizeObserver.disconnect();
    window.removeEventListener('blur', this.blur);
    this.element.remove();
  }
  private measure() {
    const bounds = this.viewport.getBoundingClientRect();
    if (bounds.width < 1 || bounds.height < 1) return;
    const width = Math.max(1, bounds.width - 28), height = Math.max(1, bounds.height - 28);
    this.fit = { x: Math.min(width, height * this.ratio), y: Math.min(height, width / this.ratio) };
    this.imageFrame.style.width = `${this.fit.x}px`;
    this.imageFrame.style.height = `${this.fit.y}px`;
    this.renderTransform();
  }
  private renderTransform() {
    const bounds = this.viewport.getBoundingClientRect();
    if (bounds.width && bounds.height) {
      const maxX = Math.max(0, (this.fit.x * this.zoom - bounds.width) / 2 + (this.zoom > 1 ? 12 : 0));
      const maxY = Math.max(0, (this.fit.y * this.zoom - bounds.height) / 2 + (this.zoom > 1 ? 12 : 0));
      this.pan = { x: clamp(this.pan.x, -maxX, maxX), y: clamp(this.pan.y, -maxY, maxY) };
    }
    this.imageFrame.style.transform = `translate(calc(-50% + ${this.pan.x}px),calc(-50% + ${this.pan.y}px)) scale(${this.zoom})`;
    this.zoomOutput.value = `${Number(this.zoom.toFixed(1))}×`;
    this.viewport.classList.toggle('ci-zoomed', this.zoom > 1.001);
    this.element.querySelector<HTMLButtonElement>('[data-inspector-control="zoom-out"]')!.disabled = this.zoom <= 1;
    this.element.querySelector<HTMLButtonElement>('[data-inspector-control="zoom-in"]')!.disabled = this.zoom >= 3;
  }
  private renderFoil() {
    if (!this.model) return;
    const intrinsic = hasFoilFinish(this.model.context.cardId, this.model.context.foil);
    const shown = this.model.preferences.foil && (this.previewFoil ?? intrinsic);
    this.imageFrame.classList.toggle('foil-preview', shown);
    const button = this.element.querySelector<HTMLButtonElement>('#foil-preview-toggle')!;
    button.disabled = !this.model.preferences.foil;
    button.setAttribute('aria-pressed', String(shown));
    button.querySelector('.ci-foil-label')!.textContent = !this.model.preferences.foil ? 'Foil effects disabled in Settings' : intrinsic ? 'Foil finish' : 'Preview foil finish';
  }
  private zoomAt(next: number, anchor: Point = { x: 0, y: 0 }) {
    next = clamp(next, 1, 3);
    const scale = next / this.zoom;
    this.pan = { x: anchor.x - (anchor.x - this.pan.x) * scale, y: anchor.y - (anchor.y - this.pan.y) * scale };
    this.zoom = next;
    if (next === 1) this.pan = { x: 0, y: 0 };
    this.renderTransform();
  }
  private relative(point: Point): Point {
    const rect = this.viewport.getBoundingClientRect();
    return { x: point.x - rect.left - rect.width / 2, y: point.y - rect.top - rect.height / 2 };
  }
  private gesturePoints() {
    const points = [...this.pointers.values()].slice(0, 2).map(point => this.relative(point));
    return { center: points.length === 2 ? { x: (points[0]!.x + points[1]!.x) / 2, y: (points[0]!.y + points[1]!.y) / 2 } : points[0] ?? { x: 0, y: 0 }, distance: points.length === 2 ? Math.max(1, distance(points[0]!, points[1]!)) : 0 };
  }
  private resetGestureBaseline() {
    const points = this.gesturePoints();
    this.gesture = { zoom: this.zoom, pan: { ...this.pan }, ...points };
  }
  private beginGesture(event: PointerEvent) {
    if (event.button !== 0 || (event.pointerType === 'mouse' && !event.isPrimary)) return;
    event.preventDefault();
    this.blockClicksUntil = performance.now() + 450;
    if(!this.pointers.size)this.quickTap={id:event.pointerId,point:{x:event.clientX,y:event.clientY}};else this.quickTap=undefined;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    try { this.viewport.setPointerCapture(event.pointerId); } catch { /* Synthetic pointers have no active capture. */ }
    this.resetGestureBaseline();
  }
  private moveGesture(event: PointerEvent) {
    if (!this.pointers.has(event.pointerId) || !this.gesture) return;
    event.preventDefault();
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if(this.quickTap&&distance(this.quickTap.point,{x:event.clientX,y:event.clientY})>8)this.quickTap=undefined;
    const current = this.gesturePoints(), start = this.gesture;
    const zoom = this.pointers.size >= 2 ? clamp(start.zoom * current.distance / Math.max(1, start.distance), 1, 3) : start.zoom;
    const scale = zoom / start.zoom;
    this.pan = { x: current.center.x - (start.center.x - start.pan.x) * scale, y: current.center.y - (start.center.y - start.pan.y) * scale };
    this.zoom = zoom;
    this.renderTransform();
  }
  private endGesture(event: PointerEvent) {
    const dismiss=event.type==='pointerup'&&this.quickTap?.id===event.pointerId&&this.pointers.size===1&&distance(this.quickTap.point,{x:event.clientX,y:event.clientY})<=8;
    this.quickTap=undefined;
    if (!this.pointers.delete(event.pointerId)) return;
    this.blockClicksUntil = performance.now() + 450;
    if (this.pointers.size) this.resetGestureBaseline(); else this.gesture = undefined;
    if(dismiss)this.callbacks.close();
  }
  private cancelGesture() {
    if (this.pointers.size || this.touching.size || this.buttonPress) this.blockClicksUntil = performance.now() + 450;
    this.quickTap=undefined;const captured = [...this.pointers.keys()];
    this.pointers.clear(); this.touching.clear(); this.gesture = undefined; this.buttonPress = undefined;
    for (const id of captured) if (this.viewport.hasPointerCapture(id)) this.viewport.releasePointerCapture(id);
  }
  private trackPointerDown(event: PointerEvent) {
    if (event.pointerType === 'touch') this.touching.add(event.pointerId);
    if (this.touching.size > 1) { this.quickTap=undefined;this.buttonPress = undefined; this.blockClicksUntil = performance.now() + 450; return; }
    const button = (event.target as Element).closest<HTMLButtonElement>('button');
    if (button && event.button === 0) this.buttonPress = { pointer: event.pointerId, start: { x: event.clientX, y: event.clientY }, button, moved: false };
  }
  private trackPointerMove(event: PointerEvent) {
    if (this.buttonPress?.pointer === event.pointerId && distance(this.buttonPress.start, { x: event.clientX, y: event.clientY }) > 8) {
      this.buttonPress.moved = true;
      this.blockClicksUntil = performance.now() + 450;
    }
  }
  private trackPointerEnd(event: PointerEvent) {
    this.touching.delete(event.pointerId);
    if (this.buttonPress?.pointer === event.pointerId && this.buttonPress.moved) this.blockClicksUntil = performance.now() + 450;
  }
  private handleClick(event: MouseEvent) {
    const button = (event.target as Element).closest<HTMLButtonElement>('button');
    if(!button){if(event.target===this.element&&performance.now()>=this.blockClicksUntil)this.callbacks.close();return;}
    if(!this.element.contains(button))return;
    // Capture ownership prevents older global modal bindings from also handling
    // this button. A pinch, canceled pointer or drag cannot become a Play click.
    event.preventDefault(); event.stopImmediatePropagation();
    if (button.disabled || (event.detail !== 0 && (performance.now() < this.blockClicksUntil || this.touching.size > 1 || this.buttonPress?.moved))) return;
    this.buttonPress = undefined;
    const control = button.dataset.inspectorControl;
    if (control === 'close') this.callbacks.close();
    else if (control === 'details') {const expanded=this.element.classList.toggle('ci-details-open');button.setAttribute('aria-expanded',String(expanded));this.cancelGesture();requestAnimationFrame(()=>this.measure());}
    else if (control === 'zoom-in') this.zoomAt(this.zoom + .5);
    else if (control === 'zoom-out') this.zoomAt(this.zoom - .5);
    else if (control === 'fit') this.zoomAt(1);
    else if (control === 'foil') { this.previewFoil = !this.imageFrame.classList.contains('foil-preview'); this.renderFoil(); }
    else if (button.dataset.action && this.model && !this.model.busy) {
      const action = this.model.actions.find(value => value.id === button.dataset.action);
      if (action) this.callbacks.action(action);
    }
  }
  private handleKey(event: KeyboardEvent) {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.cancelGesture(); this.callbacks.close(); return; }
    if (event.target === this.viewport && ['+', '=', '-', '0', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault(); event.stopPropagation();
      if (event.key === '+' || event.key === '=') this.zoomAt(this.zoom + .5);
      else if (event.key === '-') this.zoomAt(this.zoom - .5);
      else if (event.key === '0') this.zoomAt(1);
      else { this.pan.x += event.key === 'ArrowLeft' ? 35 : event.key === 'ArrowRight' ? -35 : 0; this.pan.y += event.key === 'ArrowUp' ? 35 : event.key === 'ArrowDown' ? -35 : 0; this.renderTransform(); }
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.element.querySelectorAll<HTMLElement>('button:not(:disabled),[tabindex="0"]')].filter(element => element.getClientRects().length > 0);
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !this.element.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }
}
